type DownloadStage = "generating" | "downloading" | "done";

interface DownloadFileFunOptions {
  onError?: (message: string) => void;
  onStageChange?: (stage: DownloadStage) => void;
  onDownloadProgress?: (percent: number | null) => void;
  openInNewTab?: boolean;
}

const openBlobInNewTab = (blob: Blob) => {
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, "_blank");
};

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }, 100);
};

async function DownloadFileFun(
  appId: string,
  filename: string,
  filetype: string,
  filekey: string,
  onErrorOrOptions?: ((message: string) => void) | DownloadFileFunOptions
) {
  const options: DownloadFileFunOptions =
    typeof onErrorOrOptions === "function"
      ? { onError: onErrorOrOptions }
      : onErrorOrOptions || {};
  const openInNewTab = options.openInNewTab ?? true;

  try {
    options.onStageChange?.("generating");
    const reqUrl = new URL(
      `${
        import.meta.env.VITE_API_URL
      }/applications/pdfs/fillForm/generatedPdf/${appId}`
    );
    const response = await fetch(reqUrl.toString());

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    options.onStageChange?.("downloading");

    const contentLength = Number(response.headers.get("content-length") || 0);
    const reader = response.body?.getReader();

    if (!reader) {
      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("File is empty or not found");
      }

      options.onDownloadProgress?.(100);
      if (openInNewTab) {
        openBlobInNewTab(blob);
      } else {
        triggerBrowserDownload(blob, filename);
      }
      options.onStageChange?.("done");
      return;
    }

    const chunks: ArrayBuffer[] = [];
    let receivedLength = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      if (value) {
        const normalizedChunk = new Uint8Array(value.byteLength);
        normalizedChunk.set(value);
        chunks.push(normalizedChunk.buffer);
        receivedLength += normalizedChunk.length;

        if (contentLength > 0) {
          options.onDownloadProgress?.(
            Math.min(100, Math.round((receivedLength / contentLength) * 100))
          );
        } else {
          options.onDownloadProgress?.(null);
        }
      }
    }

    const blob = new Blob(chunks, {
      type: response.headers.get("content-type") || "application/octet-stream",
    });

    if (blob.size === 0) {
      throw new Error("File is empty or not found");
    }

    options.onDownloadProgress?.(100);
    if (openInNewTab) {
      openBlobInNewTab(blob);
    } else {
      triggerBrowserDownload(blob, filename);
    }
    options.onStageChange?.("done");
  } catch (err) {
    console.error("Error downloading file:", err);
    const message =
      err instanceof Error ? err.message : "Failed to download file";
    options.onError?.(message);
  }
}

export default DownloadFileFun;
